import { useState } from 'react';
import { useQuery } from 'react-query';
import { groupBy } from 'lodash';
import {
    compareAsc,
    compareDesc,
    differenceInMilliseconds,
    isFuture,
    isPast,
    subMinutes,
} from 'date-fns';

import { getAttendeeMeetingStatus } from '../helpers';
import { MeetingStatus, ScheduledMeeting } from '../types';
import { MeetingsHttpKey } from './useMeetingsData';

const MINUTES_TO_BE_NEXTUP = 15;

interface useMeetingsGroupsProps {
    userProfileId: number;
    meetings: ScheduledMeeting[];
    isEnabled: boolean;
}

const createMeetingsGroups = (meetings: ScheduledMeeting[], userProfileId?: number) => {
    const {
        [MeetingStatus.SCHEDULED]: scheduledMeetings = [],
        [MeetingStatus.PENDING]: pendingMeetings = [],
        [MeetingStatus.CANCELLED]: cancelledMeetings = [],
    } = groupBy(meetings, meeting => meeting.status);

    const {
        upcoming: upcomingMeetings = [],
        past: historyMeetings = [],
        now: meetingNow = [],
    } = groupBy(scheduledMeetings, meeting => {
        if (isFuture(subMinutes(meeting.startDate, MINUTES_TO_BE_NEXTUP))) {
            return 'upcoming';
        } else if (isPast(meeting.endDate)) {
            return 'past';
        } else {
            return 'now';
        }
    });

    const { SENT_INVITE_TO: pendingApprovalMeetings = [], GOT_INVITE_FOR: meetingInvites = [] } =
        groupBy(pendingMeetings, meeting => getAttendeeMeetingStatus(meeting, userProfileId));

    return {
        meetingNow: meetingNow.sort((meetingA, meetingB) =>
            compareAsc(meetingA.startDate, meetingB.startDate)
        ),
        meetingInvites: meetingInvites.sort((meetingA, meetingB) =>
            compareDesc(meetingA.created, meetingB.created)
        ),
        upcomingMeetings: upcomingMeetings.sort((meetingA, meetingB) =>
            compareAsc(meetingA.startDate, meetingB.startDate)
        ),
        pendingMeetings: pendingApprovalMeetings.sort((meetingA, meetingB) =>
            compareAsc(meetingA.created, meetingB.created)
        ),
        historyMeetings: historyMeetings.sort((meetingA, meetingB) =>
            compareDesc(meetingA.startDate, meetingB.startDate)
        ),
        cancelledMeetings: cancelledMeetings.sort((meetingA, meetingB) =>
            compareAsc(meetingA.modified, meetingB.modified)
        ),
    };
};

const ONE_SECOND = 1000;
const ONE_MINUTE = 60 * ONE_SECOND;
const DEFAULT_REFETCH_INTERVAL = 5 * ONE_MINUTE;
const MIN_REFETCH_INTERVAL = 5 * ONE_SECOND;
const useMeetingsGroups = ({ userProfileId, meetings = [], isEnabled }: useMeetingsGroupsProps) => {
    const [refetchInterval, setRefetchInterval] = useState(DEFAULT_REFETCH_INTERVAL);

    return useQuery(
        [MeetingsHttpKey.MeetingsGroups, meetings, userProfileId],
        () => createMeetingsGroups(meetings, userProfileId),
        {
            enabled: isEnabled,
            refetchInterval,
            onSuccess: ({ meetingNow = [], upcomingMeetings = [] }) => {
                let minMSToRefetch = DEFAULT_REFETCH_INTERVAL;

                for (const meeting of upcomingMeetings) {
                    minMSToRefetch = Math.max(
                        MIN_REFETCH_INTERVAL,
                        Math.min(
                            differenceInMilliseconds(
                                subMinutes(meeting.startDate, MINUTES_TO_BE_NEXTUP),
                                new Date()
                            ),
                            minMSToRefetch
                        )
                    );
                }

                for (const meeting of meetingNow) {
                    if (isFuture(meeting.startDate)) {
                        minMSToRefetch = Math.max(
                            MIN_REFETCH_INTERVAL,
                            Math.min(
                                differenceInMilliseconds(meeting.startDate, new Date()),
                                minMSToRefetch
                            )
                        );
                    }

                    minMSToRefetch = Math.max(
                        MIN_REFETCH_INTERVAL,
                        Math.min(
                            differenceInMilliseconds(meeting.endDate, new Date()),
                            minMSToRefetch
                        )
                    );
                }

                setRefetchInterval(minMSToRefetch);
            },
        }
    );
};

export default useMeetingsGroups;
